/* eslint-disable react-hooks/rules-of-hooks */
import { Backdrop, Box, Button, Card, CardHeader, Checkbox, CircularProgress, Divider, IconButton, 
  Stack, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography, alpha } from "@mui/material";
import { ListContextProvider, ListControllerProps, RecordMap, useGetMainList, useListContext, useRecordContext, useRefresh } from "ra-core";
import { useCallback, useRef,FC, useMemo, useEffect, memo } from "react";
import { RecPatch } from "@/dataProvider/tools";
import { EncryptionRuleResponseDto, ExtraIgnoreItem } from "@/gen/api/encrypt";
import { DatabaseType, EditEncryptionMappingInput, EncryptMappingStatus, EncryptionMappingResponseDto, FieldMappingInput, FieldMappingResponseDto, MaskingRule, TableSchemaResponseDto } from "@/gen/api/governance";
import { useMutateEncryptionMappingsContext } from "@/pages/obfuscation/mutateEncryptionMappingsContext";
import { pages, sorts } from "@/utils/paramsWithGetMainList";
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ClearIcon from '@mui/icons-material/Clear';
import { Link as RouteLink } from 'react-router-dom'
import { dataTypeDisplayInfo } from "../../governance/fieldEdit/maskingRuleDataTypesMap";
import Create from "@/controller/Create";
import { useDefaultSavedHandlers } from "@/controller/defaultSavedHandlers";
import { usePeekEncryptSettingChangeLocked } from "@/components/obfuscation/usePeekEncryptSettingChange";
import { useHasPermit } from "@/authProvider/useHasPermit";
import { EDIT_DATA_CLASSIFICATION_ENCRYPTION } from "@/constants";
import { disabledColor } from "@/utils/a11y";
import { BigTableModeControl, buildInitialValue, useBigTableMode, useModifiers } from "./FieldsMappingList.hooks";
import WithSave from "@/controller/WithSave";
import { useCreateZTreeStore, useZSliceUnderContext, ZTreeStoreProvider } from "@/utils/zustand-form";
import { fieldValidationSchema } from "./FieldsMappingList.col.validate";
import ClassificationColumn from "./FieldsMappingList.col.classification";
import EncryptRuleColumn from "./FieldsMappingList.col.encryptRule";
import MaskingRuleColumn from "./FieldsMappingList.col.masking";
import { useEventBus } from "@/contexts/EventBusContext/useEventBus";
import { cmd_ResetProtocol, E_FieldMapping, ET_FieldMapping } from "./FieldsMappingList.events";
import { CollectEditsMetadata, CollectEditsProtocol } from "@/utils/zustand-form/ext/collectEdits";
import { FormToken, isErr, Result } from "@/utils/zustand-form/types";
import { usePlainStateFormUtils } from "@/components/input/usePlainStateFormUtils";
import { SimpleOption } from "@/components/governance/search/AmbientFacilitiesContext";
import { waitMilis } from "@/utils/asyncUtils";


const FieldsMappingList: FC = () => {
  const { fieldListCtrl } = useMutateEncryptionMappingsContext()

  const store = useCreateZTreeStore()
  
  return (
    <ZTreeStoreProvider value={store}>
      <ListContextProvider value={fieldListCtrl}>
        <FieldsMappingListBody />
      </ListContextProvider>
    </ZTreeStoreProvider>
  )
}


const FieldsMappingListBody: FC = () => {
  const hasWritePermit = useHasPermit([EDIT_DATA_CLASSIFICATION_ENCRYPTION])
  const {
    currTable,
    classifications,
  } = useMutateEncryptionMappingsContext()
  const listCtx = useListContext<FieldMappingResponseDto & RecPatch>()
  const listCtxRef = useRef(listCtx)
  listCtxRef.current = listCtx

  const databaseEm : EncryptionMappingResponseDto | undefined = useRecordContext() as any
  const { data: builtInMappingRulesBag } = useGetMainList<EncryptionRuleResponseDto & RecPatch>(
    Number(currTable?.databaseId) > 0 ? 'mapping-rules' : 'noop',
    pages(), sorts(), { dbId: currTable?.databaseId || -1 },
  )
  const builtInMappingRules = useMemo(() => Object.values(builtInMappingRulesBag || {}).sort((a, b) => a.id - b.id), [builtInMappingRulesBag])

  const prevData = useMemo(() => ({ 
    databaseId: currTable?.databaseId || 0,
    tableId: currTable?.tableId || 0, 
  }), [currTable?.databaseId, currTable?.tableId])
  const utils = useZSliceUnderContext({
    prevData,
    stageInit: () => ({}),
    extraStates: () => ({
      selectedIds: new Set<number>() // for bulk skip; both bigTableMode and normal mode 
    }),
  })
  const { states, setStates, token: rootToken, invokeProtocol, installProtocolMetadata, resetStates, hooks: { useZHasDescendantChanges, useZHasDescendantError } } = utils

  useEffect(() => {
    installProtocolMetadata<CollectEditsMetadata>(CollectEditsProtocol.metadataKey, {
      async triggerValidate() { },
      async overrideCollect(node, safeInvokeNode, safeChildrenCollection) {
        const fieldMappings : FieldMappingInput[] = []
        let _err : Result<any, any>|null = null
        for (const cnode of 
          safeChildrenCollection.values().filter(node => {
            return Object.values(node.state.touched).some(x => !!x)
          })
        ) {
          const result = await safeInvokeNode(cnode)
          if (isErr(result)) {
            _err = result
            continue;
          }
          const mapping : FieldMappingInput = result
          mapping.encryptionRuleId = (mapping.encryptionRuleId as any) === 'skip' ? null : (mapping.encryptionRuleId || null)
          mapping.maskingRule = mapping.maskingRule === MaskingRule.ReplaceWithCharacter && (mapping as any).preserveLast4
            ? MaskingRule.ReplaceWithCharacterPreserveLastFour 
            : mapping.maskingRule != null ? Number(mapping.maskingRule) : undefined
          mapping.maskingValue = mapping.maskingRule ? mapping.maskingValue : ''
          
          fieldMappings.push(mapping)
        }
        const input: EditEncryptionMappingInput = {
          databaseId: currTable?.databaseId || 0,
          tableId: currTable?.tableId || 0,
          fieldMappings,
        }
        return _err ? _err : input
      },
    })
  }, [currTable?.databaseId, currTable?.tableId, installProtocolMetadata])

  const {
    isRowSelectable,
    discardAllEdits,
    bulkSkipMappings,
  } = useModifiers(rootToken)

  const refresh = useRefresh()
  const handlers = useDefaultSavedHandlers("Encryption Mappings Update")
  const peek = usePeekEncryptSettingChangeLocked()
  const encryptSettingChangeLocked = peek(currTable?.databaseId)
  
  const {
    bigTableModeOn,
    slots,
    editorOnlyListCtx,
    modifiers: btmModifiers,
  } = useBigTableMode({isFieldMapped})

  const hasEdits = useZHasDescendantChanges() || (bigTableModeOn && (btmModifiers.getBulkSkippedData().size > 0 || btmModifiers.getStagedData().size > 0))
  const hasErrors = useZHasDescendantError()
  const getListCtx = useCallback(() => listCtxRef.current, [])

  const eventBus = useEventBus('fields-mapping-list')
  useEffect(() => {
    eventBus.subscribe(E_FieldMapping.Reset, () => {
      cmd_ResetProtocol.invoke(invokeProtocol)
      resetStates()
    })
    eventBus.subscribe(E_FieldMapping.MergeDelta, () => {
      cmd_ResetProtocol.invoke(invokeProtocol)
      resetStates()
    })
  }, [eventBus, invokeProtocol, resetStates])
  useEffect(() => {
    const t0 = eventBus.subscribe(E_FieldMapping.QueryTouched, ({resolve}: ET_FieldMapping['query_touched']) => {
      resolve(hasEdits)
    })
    return () => { eventBus.unsubscribe(t0) }
  }, [eventBus, hasEdits])

  const saveMappings = (save: (record: any, callbacks?: any) => Promise<void>) => async () => {
    if (!currTable || peek(currTable.databaseId)) { return }
    if (hasErrors) { return }

    try {
      const toSave = await CollectEditsProtocol.invoke<EditEncryptionMappingInput>(invokeProtocol)
      if (bigTableModeOn) {
        const extraBulkSkipped = btmModifiers.getBulkSkippedData().values()
        toSave.fieldMappings = [...(toSave.fieldMappings ?? []), ...extraBulkSkipped]
      }

      if (toSave.fieldMappings!.length === 0) { return }

      await save(toSave)
      refresh() // send refetch req, so the fields table list will be reset and gone while loading
      await waitMilis(1) // then, reset the previous edits, they would all be done in the background without a trace in the UI
      btmModifiers.resetStagedFields()
      discardAllEdits(true)
    } catch (error) {
      // ...
      console.log(error)
    }
  }

  const classificationSelectOptions = useMemo(() => {
    return [
      { text: '[None]', value: null, },
      ...classifications,
    ]
  }, [classifications])

  const { ids, data, loading } = !bigTableModeOn ? listCtx : (editorOnlyListCtx as ListControllerProps<FieldMappingResponseDto & RecPatch>)
  const loadingEtc = loading || !databaseEm || !currTable
  const hasBulkActions = hasWritePermit && !encryptSettingChangeLocked && !bigTableModeOn

  const rowOptions = useMemo(() => ({
    databaseEm,
    currTable,
    hasWritePermit,
    bigTableModeOn,
    hasBulkActions,
    encryptSettingChangeLocked,
  }), [bigTableModeOn, currTable, databaseEm, encryptSettingChangeLocked, hasBulkActions, hasWritePermit])
  const renderInfo = useMemo(() => ({
    classificationSelectOptions,
    builtInMappingRules,
    builtInMappingRulesBag,
    getListCtx,
  }) as RowMappingProps['renderInfo'], [builtInMappingRules, builtInMappingRulesBag, classificationSelectOptions, getListCtx])
  const setRowChecked = useCallback((fieldId, checked) => {
    setStates(v => {
      if (checked) {
        v.selectedIds.add(fieldId)
      } else {
        v.selectedIds.delete(fieldId)
      }
      return {...v}
    })
  }, [setStates])
  const selectAll = ((() => {
    if (bigTableModeOn) return {};
    const ids_ : number[] = (ids as any) ?? []
    const checked =
      ids_.length > 0 &&
      states.selectedIds.size > 0 &&
      ids_.every(id => states.selectedIds.has(id as number))
    const indeterminate = 
      ids_.some(id => states.selectedIds.has(id as number)) &&
      ids_.some(id => !states.selectedIds.has(id as number))
    const setChecked = (e:any, checked: boolean) => {
      if (!checked || indeterminate) {
        setStates(v => ({...v, selectedIds: new Set() }))
      } else {
        setStates(v => ({...v, 
          selectedIds: new Set(ids_.filter(id => isRowSelectable(data[id], true)))
        }))
      }
    }
    return { checked, setChecked, indeterminate }
  })());

  return (
    <Card>
      <CardHeader 
        title={
          !bigTableModeOn ? "Mappings" :
          <Stack component="span" direction="row" spacing={4} sx={{ mr: 'auto' }} alignItems="center">
            <Typography variant="h6">Mappings</Typography>
            <Box component="span" sx={{ border: '1px solid InactiveCaptionText', borderRadius: 0.75, p: 0.5, transform: 'scale(0.7)', lineHeight: '12px' }}>
              <Typography variant="caption" color="InactiveCaptionText">big table mode</Typography>
            </Box>
            {slots.SearchInput}
            {slots.filteredItemCount > 0 ? <Typography variant="caption" color="info.light" sx={{ml:'12px !important'}}>{slots.filteredItemCount}</Typography> : null}
          </Stack>
        } 
        action={
          hasWritePermit && !encryptSettingChangeLocked && hasEdits ?

          <Stack direction="row" spacing={2}>
            <Create resource="encryption/mappings" basePath="" {...handlers} >
              <WithSave async
                render={({save, saving}) => (
                  <>
                    <Button
                      disabled={saving}
                      variant="outlined"
                      onClick={() => {
                        if (bigTableModeOn) btmModifiers.resetStagedFields();
                        discardAllEdits()
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      disabled={saving || hasErrors}
                      variant="contained"
                      onClick={saveMappings(save)}
                    >
                      Save
                    </Button>
                  </>
                )}
              />
            </Create>
          </Stack> :
          <Tooltip title="Learn about Encryption Rules">
            <IconButton
              // @ts-expect-error hhh
              component={RouteLink}
              to="/documents/securedb/ondata_encryption/ondata_encryption_rules"
              color={"secondary" as any}
            >
              <HelpOutlineIcon />
            </IconButton>
          </Tooltip>
        }
      />
      {bigTableModeOn && slots.popoutOpen &&
        <>
          <Divider />
          <Box>
            <Stack direction="row" alignItems="center" sx={{ mx: 2 }}>
              <Typography variant="subtitle2" sx={{ mr: 'auto' }}>Choose:</Typography>
              <Button 
                onClick={() => {
                  btmModifiers.takeFilteredTrackSkip((filtered) => {
                    const toSelect : FieldMappingResponseDto[] = filtered.filter(x => isRowSelectable(x as any, true)) as any
                    if (toSelect.length === 0) { return [] }
                    return bulkSkipMappings(() => toSelect.map(x => ({
                      prevData: x,
                      stage: x,
                      errors: {},
                      touched: {},
                      metadata: {},
                    })))() as any;
                  })
                }}
              >
                Skip Below Fields
              </Button>
            </Stack>
            {slots.ListBox}
          </Box>
        </>}
      <Divider />
      <Box position="relative">
        {!bigTableModeOn && states.selectedIds.size > 0 &&
          <Stack direction="row" alignItems='center' position='absolute' width='calc(100% - 64px)' height={56}
            sx={{
              bgcolor: 'background.paper',
              left: 52,
              pl: 1.5,
              zIndex: 3,
              '& button': { mr: 2 }
            }}
          >
            <Button color="primary" variant="outlined"
              onClick={bulkSkipMappings()}
            >
              Skip
            </Button>
          </Stack>}
        <Table 
          sx={{
            '& th' : { py: 2, '& span': { display: 'inline-block' } },
            '& td' : { py: 2 },
            ...(!hasWritePermit && {
              '& input.Mui-disabled, & div.Mui-disabled': disabledColor,
            }),

            '& .column-fieldName': { minWidth: 72 },
            '& .column-dataType': { minWidth: 64 },
            '& .column-classification': { width: 200 },
            '& .column-encryptionRule': { width: 200 },
            '& .column-maskingRule': { width: 320 },
          }}
        >
          <TableHead>
            <TableRow>
              {hasBulkActions &&
                <TableCell padding="checkbox" sx={{ width: 24, padding: '0 12px 0 16px', '& > span': { p: 0 } }}>
                  <Checkbox inputProps={{ 'aria-label': 'Select all' }} color="primary"
                    className="select-all"
                    checked={selectAll.checked}
                    indeterminate={selectAll.indeterminate}
                    onChange={selectAll.setChecked}
                  />
                </TableCell>}
              <TableCell>Field Name</TableCell>
              <TableCell>Data Type</TableCell>
              <TableCell>Classification</TableCell>
              <TableCell>Encryption Rule</TableCell>
              <TableCell>Masking Rule</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(!databaseEm || !currTable) && 
              <TableCell colSpan={6} sx={{ textAlign: 'center' }}>
                <CircularProgress size={24} />
              </TableCell>}
            {!loadingEtc && ids.length === 0 && 
              <TableCell colSpan={6}>
                <Typography variant="subtitle1" textAlign="center" color="InactiveCaptionText">Empty Mapping</Typography>
              </TableCell>}

            {!loadingEtc && (ids ?? []).map((id) => data[id]).map((fieldDto) =>
              !fieldDto ? null :
              <RowMapping key={`${currTable?.tableId}-${fieldDto.fieldId}`}
                fieldDto={fieldDto}
                parentToken={rootToken}
                checked={states.selectedIds.has(fieldDto.fieldId!)}
                setChecked={setRowChecked}
                rowOptions={rowOptions}
                btmModifiers={btmModifiers}
                renderInfo={renderInfo}
              />
            )}
          </TableBody>
        </Table>
      </Box>


      <Backdrop open={listCtx.loading} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <CircularProgress />
      </Backdrop>
    </Card>
  )
}


const isFieldMapped = (f:FieldMappingResponseDto) => f.encryptMappingStatus === EncryptMappingStatus.Mapped || Boolean(f.maskingRule)

const dataTypeDisplay = (d:FieldMappingResponseDto, dbType?: DatabaseType) => {
  const info = dataTypeDisplayInfo(d, dbType)
  return (
    <Typography variant="body2"
      sx={{
        '& div': { transform: 'scale(0.8) translateX(-12px)', },
        '& span.MuiTypography-caption': { mr: 0.5, },
      }}
    >
      {(d.isPrimaryKey || d.isForeignKey || d.isIdentity || d.isComputed || d.extraIgnoreItem !== ExtraIgnoreItem.None) && <Box>
        {d.isPrimaryKey && <Typography variant="caption" color="primary">[PK]</Typography>}
        {d.isForeignKey && <Typography variant="caption" color="secondary">[FK]</Typography>}
        {d.isIdentity && <Typography variant="caption" color="InactiveCaptionText">[Identity]</Typography>}
        {d.isComputed && <Typography variant="caption" color="InactiveCaptionText">[Computed]</Typography>}
        {d.extraIgnoreItem === ExtraIgnoreItem.TransitiveIdentity && <Typography variant="caption" color="InactiveCaptionText">[Identity - transitive]</Typography>}
        <br />
      </Box>}
      {info.dataType}{info.dataTypeExt}
    </Typography>
  )
}


export default FieldsMappingList;


interface RowMappingProps {
  fieldDto: FieldMappingResponseDto, 
  parentToken?: FormToken|null|undefined, 
  checked: boolean,
  setChecked: (fieldId:number, x:boolean)=>void,
  rowOptions: {
    databaseEm: EncryptionMappingResponseDto | undefined,
    currTable: TableSchemaResponseDto | undefined,
    hasWritePermit: boolean,
    bigTableModeOn: boolean,
    hasBulkActions: boolean,
    encryptSettingChangeLocked?: boolean | undefined;
  },
  btmModifiers: BigTableModeControl['modifiers'],
  renderInfo: {
    classificationSelectOptions: SimpleOption[];
    builtInMappingRules: EncryptionRuleResponseDto[];
    builtInMappingRulesBag: RecordMap<EncryptionRuleResponseDto & RecPatch> | undefined;
    getListCtx: () => ListControllerProps<FieldMappingResponseDto & RecPatch>;
  },
}

const RowMapping = memo(function r({
  fieldDto, parentToken, checked, setChecked, rowOptions, btmModifiers, renderInfo,
}: RowMappingProps) {
  const utils = useZSliceUnderContext({
    parentToken,
    pathFragment: fieldDto.fieldId+'',
    prevData: fieldDto,
    stageInit: () => buildInitialValue(fieldDto) as FieldMappingInput,
  })
  
  const { states, setStates, installProtocolMetadata, resetStates } = utils
  const plainStateUtils = usePlainStateFormUtils(states, setStates, fieldValidationSchema, { validationContext: { fieldDto, states } })
  const { trySubmitValidate } = plainStateUtils
  
  useEffect(() => {
    installProtocolMetadata<CollectEditsMetadata>(CollectEditsProtocol.metadataKey, {
      async triggerValidate() { await trySubmitValidate() },
    })
  }, [installProtocolMetadata, trySubmitValidate])
  useEffect(() => {
    installProtocolMetadata(cmd_ResetProtocol.metadataKey, {
      action: () => { resetStates() }
    })
  }, [installProtocolMetadata, resetStates])

  const { bigTableModeOn, hasBulkActions, databaseEm, currTable, hasWritePermit } = rowOptions
  return (
    <TableRow sx={{...(bigTableModeOn && btmModifiers.isStagedField(fieldDto.fieldId!) 
      && { bgcolor: theme => alpha(theme.palette.success.light, 0.05) }
    )}}>
      {hasBulkActions &&
        <TableCell padding="checkbox" sx={{ width: 24, maxWidth: 48, padding: '0 12px 0 16px', '& > span': { p: 0 } }}>
          <Checkbox inputProps={{ 'aria-label': 'Select this row' }} color="primary"
            className="select-item"
            checked={checked}
            onChange={(e, checked) => setChecked(fieldDto.fieldId!, checked)}
          />
        </TableCell>}
      <TableCell className="column-fieldName">
        {!btmModifiers.isStagedField(fieldDto.fieldId!) 
        ? <Typography component="span" variant="body2">{fieldDto.fieldName}</Typography>
        : <Tooltip title="Remove from editor">
            <Button
              onClick={() => btmModifiers.removeFieldFromEditor(fieldDto.fieldId!)}
              startIcon={<ClearIcon />}
              sx={{
                '& .MuiSvgIcon-root': { opacity: 0, },
                '&:hover .MuiSvgIcon-root, &:focus .MuiSvgIcon-root': { opacity: 1, },
              }}
            >
              {fieldDto.fieldName}
            </Button>
          </Tooltip>}
      </TableCell>
      <TableCell className="column-dataType">
        <Typography variant="body2">{dataTypeDisplay(fieldDto, databaseEm?.databaseType)}</Typography>
      </TableCell>
      <TableCell className="column-classification">
        {currTable &&
          <ClassificationColumn fieldDto={fieldDto} 
            utils={utils}
            plainStateUtils={plainStateUtils}
            hasWritePermit={hasWritePermit} 
            {...renderInfo}
          />
        }
      </TableCell>
      <TableCell className="column-encryptionRule">
        {!currTable ? null
        : (fieldDto.isIdentity || fieldDto.isComputed || fieldDto.extraIgnoreItem !== ExtraIgnoreItem.None)
          ? <Typography variant="body2">N/A</Typography>
        : <EncryptRuleColumn fieldDto={fieldDto} hasWritePermit={hasWritePermit} 
            utils={utils}
            plainStateUtils={plainStateUtils}    
            {...renderInfo}
          />}
      </TableCell>
      <TableCell className="column-maskingRule">
        {!currTable ? null
        : (fieldDto.isIdentity || fieldDto.isComputed || fieldDto.extraIgnoreItem !== ExtraIgnoreItem.None)
          ? <Typography variant="body2">N/A</Typography>
        : <MaskingRuleColumn fieldDto={fieldDto} hasWritePermit={hasWritePermit} 
            {...renderInfo}
            utils={utils}
            plainStateUtils={plainStateUtils}    
          />}
      </TableCell>
    </TableRow>
  )
})
